/**
 * QueueBoardScreen — the waiting-room queue board (the prélèvement cockpit).
 *
 *  - Reads GET /sampling/files (priority-ordered live tickets) + GET /sampling/salles (room picker).
 *  - Call next / call one / mark absent / draw / route / finish — each gated by its capability and
 *    only valid for the ticket's current statut (mirrors the server transition machine).
 *  - Full 5-state DoD: offline (cache-aware), loading, error, forbidden (route-level), empty.
 *  - Keyboard-first: "Appeler le suivant" is the primary action; row actions are real <button>s.
 */
import { useMemo, useState } from "react";
import "../sampling.css";
import { Button, Card, ErrorState, OfflineState, Select, Table, useToast } from "@/ui";
import type { Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { type ApiError } from "@/api";
import { token } from "@/design/theme";
import { CAP } from "../capabilities";
import {
  useAbsent,
  useAcheminer,
  useAppeler,
  useAppelerSuivant,
  useBoard,
  useRooms,
  useTerminer,
} from "../hooks";
import type { BoardTicket } from "../types";
import { PrioriteBadge, StatutBadge } from "../components/labels";
import { DrawDialog } from "../components/DrawDialog";

export function QueueBoardScreen() {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);

  const canUpdate = useCan(CAP.prelevementUpdate);
  const canCreate = useCan(CAP.prelevementCreate);

  const [roomId, setRoomId] = useState<string | null>(null);
  const [drawTicket, setDrawTicket] = useState<BoardTicket | null>(null);

  const rooms = useRooms();
  const board = useBoard(roomId);

  const appelerSuivant = useAppelerSuivant();
  const appeler = useAppeler();
  const absent = useAbsent();
  const acheminer = useAcheminer();
  const terminer = useTerminer();

  const onError = (e: ApiError) => toast.error(e.message, e.code);

  const tickets = board.data?.tickets ?? [];
  const waiting = tickets.length;

  const roomOptions = useMemo(
    () => (rooms.data ?? []).map((r) => ({ value: r.id, label: r.nom })),
    [rooms.data],
  );

  const callNext = () => {
    const targetRoom = roomId ?? board.data?.waiting_room_id;
    if (!targetRoom) return;
    appelerSuivant.mutate(
      { waiting_room_id: targetRoom },
      {
        onSuccess: (res) => {
          const n = (res as { numero?: number } | null)?.numero;
          if (n == null) toast.warn(t("sampling.queue.callNext.none"));
          else toast.success(t("sampling.queue.callNext.success", { n }));
        },
        onError,
      },
    );
  };

  const columns: ReadonlyArray<Column<BoardTicket>> = useMemo(
    () => [
      {
        key: "numero",
        header: t("sampling.ticket.numero"),
        width: "5rem",
        sortable: true,
        sortAccessor: (r) => r.numero,
        cell: (r) => (
          <span className={r.priorite === "urgent" ? "smp-numero smp-numero--urgent" : "smp-numero"}>
            {r.numero}
          </span>
        ),
      },
      {
        key: "priorite",
        header: t("sampling.priorite.routine"),
        cell: (r) => <PrioriteBadge priorite={r.priorite} />,
      },
      {
        key: "patient",
        header: t("sampling.ticket.patient"),
        cell: (r) => r.patient_nom ?? <span style={{ color: token.steel }}>{t("sampling.ticket.anonyme")}</span>,
      },
      {
        key: "tubes",
        header: t("sampling.ticket.tubes"),
        mono: true,
        cell: (r) => (
          <span className="nv-mono nv-ltr-island">{(r.tubes ?? []).filter((tt) => tt.statut === "attache").length}</span>
        ),
      },
      {
        key: "statut",
        header: t("sampling.queue.room"),
        cell: (r) => <StatutBadge statut={r.statut} />,
      },
      {
        key: "actions",
        header: t("sampling.col.actions"),
        align: "end",
        cell: (r) => <RowActions ticket={r} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canUpdate, canCreate, appeler.isPending, absent.isPending, acheminer.isPending, terminer.isPending],
  );

  // ── the 5 states ──────────────────────────────────────────────────────────────
  if (!online) return <OfflineState onRetry={() => void board.refetch()} cached={!!board.data} />;

  if (board.isError && !board.data) {
    return (
      <ErrorState
        message={board.error.message}
        code={board.error.code}
        onRetry={() => void board.refetch()}
      />
    );
  }

  function RowActions({ ticket }: { ticket: BoardTicket }) {
    const s = ticket.statut;
    return (
      <div className="smp-actions">
        {canUpdate && s === "en_attente" && (
          <Button
            size="sm"
            variant="secondary"
            loading={appeler.isPending && appeler.variables?.ticketId === ticket.id}
            onClick={() =>
              appeler.mutate(
                { ticketId: ticket.id },
                { onSuccess: () => toast.success(t("sampling.queue.called.success", { n: ticket.numero })), onError },
              )
            }
          >
            {t("sampling.queue.call")}
          </Button>
        )}
        {canUpdate && s === "appele" && (
          <Button
            size="sm"
            variant="ghost"
            loading={absent.isPending && absent.variables?.ticketId === ticket.id}
            onClick={() =>
              absent.mutate(
                { ticketId: ticket.id },
                { onSuccess: () => toast.success(t("sampling.queue.absent.success", { n: ticket.numero })), onError },
              )
            }
          >
            {t("sampling.queue.absent")}
          </Button>
        )}
        {canCreate && s === "appele" && (
          <Button size="sm" variant="primary" onClick={() => setDrawTicket(ticket)}>
            {t("sampling.queue.draw")}
          </Button>
        )}
        {canUpdate && s === "preleve" && (
          <Button
            size="sm"
            variant="secondary"
            loading={acheminer.isPending && acheminer.variables?.ticketId === ticket.id}
            onClick={() =>
              acheminer.mutate(
                { ticketId: ticket.id },
                { onSuccess: () => toast.success(t("sampling.queue.routed.success", { n: ticket.numero })), onError },
              )
            }
          >
            {t("sampling.queue.route")}
          </Button>
        )}
        {canUpdate && s === "achemine" && (
          <Button
            size="sm"
            variant="ghost"
            loading={terminer.isPending && terminer.variables?.ticketId === ticket.id}
            onClick={() =>
              terminer.mutate(
                { ticketId: ticket.id },
                { onSuccess: () => toast.success(t("sampling.queue.finished.success", { n: ticket.numero })), onError },
              )
            }
          >
            {t("sampling.queue.finish")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="smp-page">
      <header className="smp-head">
        <h1 className="smp-head__title">{t("sampling.queue.title")}</h1>
        <span className="smp-head__spacer" />
        {board.data && (
          <span className="nv-mono nv-ltr-island" style={{ color: token.slate }}>
            {t("sampling.queue.waiting", { n: waiting })}
          </span>
        )}
      </header>

      <Card>
        <div className="smp-toolbar">
          {roomOptions.length > 0 && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: token.s2 }}>
              <span style={{ color: token.slate }}>{t("sampling.queue.room")}</span>
              <Select
                value={roomId ?? ""}
                onChange={(e) => setRoomId(e.target.value || null)}
                options={roomOptions}
                aria-label={t("sampling.queue.room")}
                {...(board.data?.salle ? { placeholder: board.data.salle } : {})}
              />
            </label>
          )}
          <span className="smp-head__spacer" />
          {canUpdate && (
            <Button variant="primary" onClick={callNext} loading={appelerSuivant.isPending}>
              {t("sampling.queue.callNext")}
            </Button>
          )}
        </div>
      </Card>

      <Table<BoardTicket>
        columns={columns}
        rows={tickets}
        rowKey={(r) => r.id}
        caption={t("sampling.queue.title")}
        loading={board.isLoading}
        {...(board.isError && board.data
          ? { error: { message: board.error.message, onRetry: () => void board.refetch() } }
          : {})}
        emptyTitle={t("sampling.queue.empty")}
        emptyDescription={t("sampling.queue.emptyDesc")}
        {...(canUpdate
          ? {
              emptyAction: (
                <Button variant="primary" onClick={callNext} loading={appelerSuivant.isPending}>
                  {t("sampling.queue.callNext")}
                </Button>
              ),
            }
          : {})}
      />

      {drawTicket && (
        <DrawDialog ticket={drawTicket} open={!!drawTicket} onClose={() => setDrawTicket(null)} />
      )}
    </div>
  );
}
